import googleCalendarIcon from '@assets/svg/google-calendar-icon.svg'
import { useUser } from '@features/auth/hooks'
import { createGoogleCalendarUrl } from '@features/event/utils/create-google-calendar-url'
import dayjs from 'dayjs'
import { eventApi, EventResponse, useEventParticipateInEventMutation } from '@app/services'
import { Button, Stack } from '@common/components'
import { EVENT_ROUTE, SIMPLE_LOGIN_ROUTE } from '@common/constants/routes'
import { useAppDispatch } from '@common/hooks'
import { CheckCircleOutlineIcon } from '@common/icons'
import {
  requestNotificationPermission,
  scheduleEventNotification,
} from '@common/utils/notifications'
import { useNavigate } from '@lib/router'

const ParticipantButton = (props: {
  isDashboard?: boolean
  event: EventResponse
  width?: string
  size?: 'small' | 'medium'
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  onParticipate?: () => void
}) => {
  const { width = '180px', isDashboard = false, event, size, direction, onParticipate } = props
  const { user } = useUser()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [participateInEvent] = useEventParticipateInEventMutation()

  const handleParticipate = async (eventId: string) => {
    const patchResult = dispatch(
      eventApi.util.updateQueryData(
        'eventGetEvents',
        {
          isDashboard,
        },
        (draft) => {
          draft.events
            ?.find((e) => e.event_id === eventId)
            ?.participants.push({ id: user?.id, username: user?.username, image: user?.image })
        },
      ),
    )
    try {
      await participateInEvent({ eventId: eventId }).unwrap()
      onParticipate?.()
      // Request notification permission and schedule notifications
      const permission = await requestNotificationPermission()
      if (permission === 'granted') {
        const startTime = dayjs.utc(event.start_time_utc).local()

        // Schedule 5-minute reminder
        scheduleEventNotification(
          event.event_id,
          `${event.event_title} starts in 5 minutes!`,
          startTime.subtract(5, 'minutes').toDate(),
        )

        // Schedule start time notification
        scheduleEventNotification(
          `${event.event_id}-start`,
          `${event.event_title} is starting now!`,
          startTime.toDate(),
        )
      }
    } catch {
      patchResult.undo()
    }
  }
  return (
    <>
      {user && event.participants.find((userp) => userp.id === user?.id) ? (
        <Stack
          direction={direction || { xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ ml: 1, width: { xs: '80px', sm: 'auto' } }}
        >
          {!event?.is_live && (
            <Button
              size={size === 'small' ? 'small' : 'medium'}
              variant='contained'
              startIcon={
                <img
                  src={googleCalendarIcon}
                  alt='google calendar'
                  style={{ width: '16px', height: '16px' }}
                />
              }
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault()
                window.open(createGoogleCalendarUrl(event), '_blank')
              }}
              sx={{
                width: { xs: '80px', sm: 'auto' },
                backgroundColor: '#0061FD',
                color: '#fff',
                fontWeight: 600,
                fontSize: { xs: '10px', sm: size === 'small' ? '10px' : '14px' },
              }}
            >
              Add to Calendar
            </Button>
          )}
          <Button
            size={size === 'small' ? 'small' : 'medium'}
            variant='contained'
            color={event?.is_live ? 'primary' : 'success'}
            disableElevation
            disableRipple
            onClick={() => {
              navigate(`${EVENT_ROUTE}/${event.short_event_id ?? event.event_id}`)
            }}
            sx={{
              width: { xs: '80px', sm: width },
              fontSize: { xs: '10px', sm: size === 'small' ? '10px' : '14px' },
              px: 0,
              borderLeft: '4px solid rgba(0, 0, 0, .2)',
              borderTopLeftRadius: ' 6px',
              borderBottomLeftRadius: ' 6px',
            }}
          >
            {event?.is_live ? (
              'Jump in Now'
            ) : (
              <>
                <CheckCircleOutlineIcon
                  sx={{ display: { xs: 'none', md: size === 'small' ? 'none' : 'flex', mr: 0.5 } }}
                />{' '}
                {'  '} You&apos;re going!
              </>
            )}
          </Button>
        </Stack>
      ) : (
        <Button
          floating
          highlighted
          // loading={isLoadingParticipateInEvent}
          color={event?.is_live ? 'secondary' : 'neutral'}
          variant='outlined'
          sx={{
            zIndex: 10,
            width: width,
            fontWeight: 600,
            py: 1,
            fontSize: { xs: '10px', sm: size === 'small' ? '10px' : '14px' },
            // borderRadius: '5px',
            // width: '100px',
            // height: '40px',
            // fontSize: '16px',
            // color: '#ff9641',
            // background: (theme) => (theme.palette.mode === 'dark' ? '#38281ED9' : 'white'),
            // ':hover': {
            //   background: (theme) => (theme.palette.mode === 'dark' ? '#38281E80' : '#FFFFFF80'),
            // },
          }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault()
            if (!user) {
              navigate(SIMPLE_LOGIN_ROUTE)
              return
            }

            if (event?.is_live) {
              navigate(`${EVENT_ROUTE}/${event.short_event_id ?? event.event_id}`)
            } else {
              handleParticipate(event.event_id)
            }
          }}
        >
          Attend
        </Button>
      )}
    </>
  )
}

export default ParticipantButton
